import { useState, type CSSProperties } from 'react';
import { ChevronDown, ChevronUp, Mic, MessageSquare, MoreHorizontal, Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GenieAvatar } from '@/components/GenieAvatar';
import type { WidgetTemplateId, WidgetStyleConfig } from '@/types/ui/widget-styles';
import { getWidgetTemplate } from '@/data/widget-templates';
import { BUTTON_SIZE_MAP, BUTTON_SHAPE_RADIUS, WIDGET_SIZE_PRESETS, SHADOW_PRESETS } from '@/types/ui/widget-styles';
import { deriveSecondary } from '@/pages/agent-interfaces/live-agent/components/GenieChatInputWeb';
import { widgetBackgroundToCss } from '@/lib/widgetBackgroundPresets';
import { ctaBorderRadius, resolveCtaColors } from '@/lib/ctaButtonStyle';
import type { EmbedStyle } from '@/types/ui/branding';

import type { WidgetPreviewProps } from "@/types/chat-widget";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const WidgetPreview = ({ chatOnly = false, genie, selectedTemplate, primaryColor, embedStyle, avatarImageUrl }: WidgetPreviewProps) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(true);
  const [openPreviewTab, setOpenPreviewTab] = useState<'start' | 'talking' | 'chat'>('start');
  const template = getWidgetTemplate(selectedTemplate);
  const config = template?.config;

  if (!config) return null;

  const buttonSize = BUTTON_SIZE_MAP[config.button.size];
  const buttonRadius = BUTTON_SHAPE_RADIUS[config.button.shape];
  const popupSize = WIDGET_SIZE_PRESETS[config.popup.size];
  const shadow = SHADOW_PRESETS[config.popup.shadowIntensity];
  const isDark = config.theme.style === 'dark';
  const isGlass = config.theme.glassEffect;
  const avatarUrl = genie.branding?.logoUrl || genie.branding?.customAvatarUrl;

  // Calculate button color based on theme
  const buttonBgColor = isDark ? '#374151' : `${primaryColor}15`;
  const popupBg = isDark ? '#1f2937' : isGlass ? 'rgba(255,255,255,0.85)' : 'white';
  const textColor = isDark ? '#f9fafb' : '#111827';
  const subtextColor = isDark ? '#9ca3af' : '#6b7280';

  // Render the floating button based on template type
  const renderButton = () => {
    // Banner style - full width bar
    if (selectedTemplate === 'banner') {
      return (
        <div
          className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 py-3"
          style={{
            backgroundColor: isDark ? '#374151' : 'white',
            borderTop: '1px solid #e5e7eb',
            boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10">
              <GenieAvatar color={primaryColor} size={40} customAvatarUrl={avatarUrl} />
            </div>
            <span className="text-sm font-medium" style={{ color: textColor }}>
              {config.button.labelText || 'Need help? Talk to our assistant'}
            </span>
          </div>
          <button
            className="px-4 py-2 rounded-full text-white text-sm font-medium"
            style={{ backgroundColor: primaryColor }}
          >
            Start Chat
          </button>
        </div>
      );
    }

    // Side tab style
    if (selectedTemplate === 'side-tab') {
      return (
        <div
          className="absolute top-1/2 -right-1 -translate-y-1/2 px-2 py-4 rounded-l-lg shadow-lg cursor-pointer"
          style={{
            backgroundColor: primaryColor,
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
        >
          <span className="text-white font-medium text-sm">
            {config.button.labelText || 'Help'}
          </span>
        </div>
      );
    }

    // Pill button with text
    if (selectedTemplate === 'pill-button') {
      return (
        <div
          className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg cursor-pointer"
          style={{ backgroundColor: primaryColor }}
        >
          <div className="w-8 h-8">
            <GenieAvatar color="#ffffff" size={32} customAvatarUrl={avatarUrl} />
          </div>
          <span className="text-white font-medium">
            {config.button.labelText || 'Chat with us'}
          </span>
          {config.button.showOnlineIndicator && (
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          )}
        </div>
      );
    }

    // Default circular/square button
    return (
      <div className="absolute bottom-4 right-4">
        <div
          className="flex items-center justify-center cursor-pointer transition-transform hover:scale-110 relative"
          style={{
            width: buttonSize,
            height: buttonSize,
            borderRadius: buttonRadius,
            backgroundColor: buttonBgColor,
            boxShadow: `0 4px 20px ${primaryColor}30`,
            backdropFilter: isGlass ? 'blur(10px)' : undefined,
            border: isGlass ? `1px solid ${primaryColor}30` : undefined,
          }}
        >
          <div style={{ width: buttonSize * 0.7, height: buttonSize * 0.7 }}>
            <GenieAvatar color={primaryColor} size={buttonSize * 0.7} customAvatarUrl={avatarUrl} />
          </div>
          {config.button.showOnlineIndicator && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse" />
          )}
        </div>
      </div>
    );
  };

  // Render the open widget popup.
  // Every template now serves the SAME LiveAgent interior, so the preview
  // shows one canonical popup regardless of which style is selected. Mirrors
  // the actual production widget: no chrome header (just floating close/expand
  // iconettes top-right), magical avatar with brand-gradient Start CTA, AND a
  // persistent text input pill so users see type-or-talk upfront.
  const renderUnifiedLiveAgentPopup = () => {
    // HG brand blue snaps to HG pink; custom brands derive an analogous accent.
    // Mirrors GenieChatInputWeb's smart fallback so the preview stays on-brand.
    const accentColor = primaryColor === '#4E9CFF' ? '#FF4DB8' : deriveSecondary(primaryColor);
    const popupWidth = 180;
    // Real medium popup preset is 380x580 (WIDGET_SIZE_PRESETS.medium) — same
    // aspect ratio here so the now-correctly-sized avatar/CTA/action row don't
    // get cramped or push the footer out of view like a shorter box would.
    const popupHeight = Math.round(popupWidth * (580 / 380));
    // Scale real production px values (header logo, pattern tile size) down
    // to this preview's much smaller popup (180px vs. ~380px in production),
    // so size changes stay visible/proportional without breaking the tiny
    // popup's layout.
    const PREVIEW_SCALE = popupWidth / 380;
    const scaledBackground = embedStyle?.background
      ? { ...embedStyle.background, patternSize: (embedStyle.background.patternSize ?? undefined) && Math.max(1, Math.round(embedStyle.background.patternSize! * PREVIEW_SCALE)) }
      : embedStyle?.background;
    const bodyStyle = widgetBackgroundToCss(scaledBackground, 'white');
    const cta = resolveCtaColors({
      brandColor: primaryColor,
      gradientEnd: accentColor,
      connected: false,
      gradient: true,
      colorOverride: embedStyle?.ctaButton?.color,
      colorOverride2: embedStyle?.ctaButton?.color2,
      textColorOverride: embedStyle?.ctaButton?.textColor,
    });
    const ctaRadius = ctaBorderRadius(embedStyle?.buttonStyle);
    const actionBg = embedStyle?.actionButtons?.idleBackgroundColor || `${primaryColor}12`;
    const actionColor = embedStyle?.actionButtons?.iconColor || primaryColor;
    const headerStyle = embedStyle?.headerStyle;
    const headerStart = headerStyle?.color || primaryColor;
    const headerEnd = headerStyle?.color2 || accentColor;
    const headerBg: CSSProperties = headerStyle?.transparent
      ? { background: 'transparent' }
      : { background: `linear-gradient(135deg, ${headerStart} 0%, ${headerEnd} 100%)` };
    const headerLogoSize = Math.round((embedStyle?.headerLogoSize ?? 36) * PREVIEW_SCALE);
    const avatarScale = embedStyle?.avatarSizeScale ?? 1;
    const ctaScale = embedStyle?.ctaButton?.sizeScale ?? 1;
    const chatScale = embedStyle?.chatButton?.sizeScale ?? 1;
    const actionScale = embedStyle?.actionButtons?.sizeScale ?? 1;

    const chatStyle = embedStyle?.chatStyle;
    const emptyStateOrder = chatStyle?.elementOrder ?? ['icon', 'title', 'subtitle'];
    const emptyStateHidden = new Set(chatStyle?.hiddenElements ?? []);
    const alignClass = (el: 'icon' | 'title' | 'subtitle') => {
      const align = chatStyle?.elementAlign?.[el] ?? 'center';
      return align === 'left' ? 'items-start text-left' : align === 'right' ? 'items-end text-right' : 'items-center text-center';
    };

    // Shared avatar circle — identical in the Start (idle) and Talking
    // (connected) tabs; only the row beneath it differs.
    const avatarNode = (
      <div
        className="rounded-full mb-2 flex items-center justify-center flex-shrink-0"
        style={{
          // Real widget (EmbedLiveAgentExperience): VoiceAgentAvatar is a
          // fixed 200px outer glow ring (150px for the "small" popup
          // preset) — not a small badge scaled off some tiny base — with
          // a 160px (0.8x) inner avatar. Scaled down via PREVIEW_SCALE so
          // it occupies the same large proportion of the popup here.
          width: 200 * PREVIEW_SCALE * avatarScale,
          height: 200 * PREVIEW_SCALE * avatarScale,
          backgroundColor: `${primaryColor}18`,
          boxShadow: `0 0 10px ${primaryColor}33`,
        }}
      >
        <GenieAvatar
          color={primaryColor}
          avatarBackgroundOpacity={genie.branding?.avatarBackgroundOpacity}
          size={160 * PREVIEW_SCALE * avatarScale}
          customAvatarUrl={avatarUrl}
          coverImageUrl={avatarImageUrl}
          showLogoOverlay={embedStyle?.avatarShowLogoOverlay}
        />
      </div>
    );

    return (
      <div
        className="absolute bottom-2 right-2 overflow-hidden"
        style={{
          width: popupWidth,
          height: popupHeight,
          borderRadius: 16,
          ...bodyStyle,
          boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
        }}
      >
        <div className="relative h-full flex flex-col">
          {/* Header bar — mirrors EmbedLiveAgentLayout's real header: small
              genie mark, agent name, and close/open-in-new icons. Shown in
              both start and chat states, matching production. */}
          <div className="flex items-center gap-1 px-1.5 py-1 flex-shrink-0" style={headerBg}>
            <div
              className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/25"
              style={{ width: headerLogoSize, height: headerLogoSize }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <img src="/helpgenie_logo.png" alt="" className="h-full w-full object-contain" />
              )}
            </div>
            <span className="flex-1 truncate text-[7px] font-semibold text-white">{genie.name}</span>
            <div className="flex items-center gap-0.5">
              <div className="w-2.5 h-2.5 rounded-full bg-white/20 flex items-center justify-center text-[6px] text-white">↗</div>
              <div className="w-2.5 h-2.5 rounded-full bg-white/20 flex items-center justify-center text-[6px] text-white">×</div>
            </div>
          </div>

          {!chatOnly && openPreviewTab === 'start' ? (
            <>
              {/* LiveAgent body — status + avatar + idle Talk/Chat buttons.
                  Real widget (EmbedLiveAgentExperience): this idle pair is
                  swapped out entirely once connected — see the "Talking" tab
                  — never shown together with the connected pill. */}
              <div className="flex-1 flex flex-col items-center justify-center px-2 pt-3 pb-1">
                {!embedStyle?.hideWelcomeMessage && (
                  <p className="text-[7px] text-center text-gray-500 mb-1.5">
                    Hey! I'm {genie.name}
                  </p>
                )}
                {avatarNode}
                {/* Real idle row (StudioActionCluster / EmbedLiveAgentExperience):
                    a gradient "Talk" pill next to an outlined "Chat" pill — not a
                    single "Start Conversation" CTA. Scaled the same way the old
                    single button was, via PREVIEW_SCALE × ctaScale. */}
                <div className="flex items-center gap-1" style={{ gap: 4 * PREVIEW_SCALE * ctaScale }}>
                  <div
                    className="flex items-center font-semibold"
                    style={{
                      gap: 3 * PREVIEW_SCALE * ctaScale,
                      background: cta.background,
                      color: cta.color,
                      borderRadius: ctaRadius,
                      boxShadow: `0 4px 10px ${cta.start}55`,
                      padding: `${8 * PREVIEW_SCALE * ctaScale}px ${14 * PREVIEW_SCALE * ctaScale}px`,
                      fontSize: `${12.5 * PREVIEW_SCALE * ctaScale}px`,
                      lineHeight: 1.25,
                    }}
                  >
                    <Mic size={10 * PREVIEW_SCALE * ctaScale} strokeWidth={2.25} />
                    Talk
                  </div>
                  <div
                    className="flex items-center font-semibold border"
                    style={{
                      gap: 3 * PREVIEW_SCALE * chatScale,
                      borderColor: embedStyle?.chatButton?.color || "#D1D5DB",
                      color: embedStyle?.chatButton?.color || "#374151",
                      backgroundColor: embedStyle?.chatButton?.backgroundColor || undefined,
                      borderRadius: ctaRadius,
                      padding: `${8 * PREVIEW_SCALE * chatScale}px ${14 * PREVIEW_SCALE * chatScale}px`,
                      fontSize: `${12.5 * PREVIEW_SCALE * chatScale}px`,
                      lineHeight: 1.25,
                    }}
                  >
                    <MessageSquare size={10 * PREVIEW_SCALE * chatScale} strokeWidth={2.25} />
                    Chat
                  </div>
                </div>
              </div>
            </>
          ) : !chatOnly && openPreviewTab === 'talking' ? (
            <>
              {/* Connected voice state — avatar + status line + the single
                  Mic/Chat/Stop/More pill. The idle Talk/Chat pair from the
                  Start tab is gone entirely here, matching production: the
                  two states never render together. */}
              <div className="flex-1 flex flex-col items-center justify-center px-2 pt-3 pb-1">
                <p className="text-[7px] text-center text-gray-500 mb-1.5">
                  Genie is listening…
                </p>
                {avatarNode}
                <div
                  className="mt-2 flex items-center rounded-full"
                  style={{ gap: 3 * actionScale, padding: 2 * actionScale, backgroundColor: `${primaryColor}0d` }}
                >
                  <div
                    className="rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ width: 16 * actionScale, height: 16 * actionScale, color: primaryColor }}
                    title="Mic"
                  >
                    <Mic size={8 * actionScale} strokeWidth={2.25} />
                  </div>
                  <div
                    className="rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ width: 16 * actionScale, height: 16 * actionScale, color: primaryColor }}
                    title="Show chat"
                  >
                    <MessageSquare size={8 * actionScale} strokeWidth={2.25} />
                  </div>
                  <div
                    className="rounded-full flex items-center justify-center text-white flex-shrink-0"
                    style={{
                      width: 16 * actionScale,
                      height: 16 * actionScale,
                      background: `linear-gradient(135deg, ${primaryColor}40, ${accentColor}30)`,
                      color: primaryColor,
                    }}
                    title="Stop"
                  >
                    <Square size={7 * actionScale} strokeWidth={2.25} fill="currentColor" />
                  </div>
                  <div
                    className="rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ width: 16 * actionScale, height: 16 * actionScale, backgroundColor: actionBg, color: actionColor }}
                    title="More actions"
                  >
                    <MoreHorizontal size={8 * actionScale} strokeWidth={2.25} />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Chat view — empty state (icon/title/subtitle, per chatStyle)
                  over the popup background, plus the styled text input. */}
              <div className="flex-1 flex flex-col items-center justify-center px-2 py-2 overflow-hidden">
                {emptyStateOrder
                  .filter((el) => !emptyStateHidden.has(el))
                  .map((el) => {
                    if (el === 'icon') {
                      return (
                        <div key="icon" className={`flex w-full flex-col mb-1.5 ${alignClass('icon')}`}>
                          <span
                            className="flex h-6 w-6 items-center justify-center rounded-lg text-[10px]"
                            style={{ background: `linear-gradient(135deg, ${primaryColor}14, ${accentColor}14)` }}
                          >
                            {/* The popup chat shows the genie's logo, the lamp only without one. */}
                            {genie.branding?.logoUrl ? (
                              <img src={genie.branding.logoUrl} alt="" className="h-3 w-3 object-contain" />
                            ) : (
                              <GenieIcon name="genie-lamp" size={12} />
                            )}
                          </span>
                        </div>
                      );
                    }
                    if (el === 'title') {
                      return (
                        <div key="title" className={`flex w-full flex-col mb-0.5 ${alignClass('title')}`}>
                          <p className="text-[6.5px] font-semibold text-gray-800">
                            {chatStyle?.titleText || `Ask ${genie.name} anything`}
                          </p>
                        </div>
                      );
                    }
                    return (
                      <div key="subtitle" className={`flex w-full flex-col ${alignClass('subtitle')}`}>
                        <p className="text-[5.5px] leading-snug text-gray-500">
                          {chatStyle?.subtitleText ||
                            (chatOnly ? 'Type a question to get started.' : 'Type a question, or just speak — the conversation flows here either way.')}
                        </p>
                      </div>
                    );
                  })}
              </div>

              <div className="px-2 pb-1.5">
                <div
                  className="rounded-full"
                  style={
                    chatStyle?.hideBorder
                      ? undefined
                      : {
                          padding: '1px',
                          background: `linear-gradient(to right, ${chatStyle?.borderColor || primaryColor}, ${chatStyle?.borderColor2 || chatStyle?.borderColor || accentColor})`,
                        }
                  }
                >
                  <div
                    className="flex items-center gap-1 rounded-full px-1 py-0.5"
                    style={{ backgroundColor: chatStyle?.inputBackgroundColor || 'white', boxShadow: '0 1px 3px rgba(15,23,42,0.12)' }}
                  >
                    <div
                      className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full text-[6px]"
                      style={{ backgroundColor: `${chatStyle?.plusButtonColor || primaryColor}14`, color: chatStyle?.plusButtonColor || primaryColor }}
                    >
                      +
                    </div>
                    <span className="flex-1 truncate text-[6px]" style={{ color: chatStyle?.placeholderColor || '#9CA3AF' }}>
                      Type a message…
                    </span>
                    <div
                      className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full text-[6px]"
                      style={
                        chatStyle?.sendButtonUseActionStyle
                          ? {
                              backgroundColor: embedStyle?.actionButtons?.activeBackgroundColor || primaryColor,
                              color: embedStyle?.actionButtons?.iconColor || 'white',
                            }
                          : {
                              background: `linear-gradient(90deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                              color: 'white',
                            }
                      }
                    >
                      ➤
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Powered by footer */}
          <div className="px-2 py-1 border-t border-gray-100 flex items-center gap-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
              }}
            />
            <span className="text-[6px]" style={{ color: primaryColor }}>
              Made magical by Help Genie
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPopup = () => {
    // Unified interior for every template — bubble style differs, popup does not.
    return renderUnifiedLiveAgentPopup();
  };

  // Keep small/medium/large in sync with CAPTION_SIZE_PRESETS in public/widget.js
  // and PEEKOUT_SIZE_PRESETS in public/widget-v2.js.
  const CAPTION_SIZE_STYLES = {
    small: { fontSize: 9, padding: '5px 8px' },
    medium: { fontSize: 10, padding: '7px 10px' },
    large: { fontSize: 11.5, padding: '9px 12px' },
  } as const;

  // Caption bubble next to the closed-state button — only relevant for the
  // standard floating button and pill-button templates (banner/side-tab have
  // different geometry and aren't wired up to a caption in widget.js yet).
  const renderCaption = () => {
    const caption = embedStyle?.caption;
    if (!caption?.enabled || !caption.text) return null;
    if (selectedTemplate === 'banner' || selectedTemplate === 'side-tab') return null;
    const sizeStyle = CAPTION_SIZE_STYLES[caption.size ?? 'medium'];
    return (
      <div
        className="absolute bottom-6 right-[52px] shadow-lg whitespace-nowrap"
        style={{
          background: caption.backgroundColor || '#FFFFFF',
          color: caption.textColor || '#1F2937',
          borderRadius: '10px 10px 2px 10px',
          fontWeight: 500,
          ...sizeStyle,
        }}
      >
        {caption.text}
      </div>
    );
  };

  // Legacy per-template popup paths kept below for reference; no longer reached.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _renderPopupLegacy = () => {
    // Immersive mode - larger voice-focused UI
    if (selectedTemplate === 'immersive') {
      return (
        <div
          className="absolute bottom-2 right-2 overflow-hidden"
          style={{
            width: 160,
            height: 200,
            borderRadius: config.popup.borderRadius,
            backgroundColor: popupBg,
            boxShadow: shadow,
            backdropFilter: isGlass ? 'blur(20px)' : undefined,
          }}
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div
              className="flex items-center gap-2 px-3 py-2 border-b"
              style={{
                backgroundColor: config.theme.accentOnHeader ? `${primaryColor}15` : 'transparent',
                borderColor: isDark ? '#374151' : '#e5e7eb',
              }}
            >
              <div className="w-6 h-6">
                <GenieAvatar color={primaryColor} size={24} customAvatarUrl={avatarUrl} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: textColor }}>
                  {genie.name}
                </p>
              </div>
            </div>

            {/* Voice interface */}
            <div className="flex-1 flex flex-col items-center justify-center p-3">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                style={{
                  backgroundColor: `${primaryColor}15`,
                  boxShadow: `0 0 20px ${primaryColor}30`,
                }}
              >
                <GenieAvatar color={primaryColor} size={56} customAvatarUrl={avatarUrl} />
              </div>
              <p className="text-[10px] text-center mb-3" style={{ color: subtextColor }}>
                {genie.name}
              </p>
              <button
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-[10px] font-medium"
                style={{ backgroundColor: primaryColor }}
              >
                <Mic className="w-3 h-3" />
                Start Voice Chat
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Standard popup
    return (
      <div
        className="absolute bottom-2 right-2 overflow-hidden"
        style={{
          width: 140,
          height: 180,
          borderRadius: config.popup.borderRadius,
          backgroundColor: popupBg,
          boxShadow: shadow,
          backdropFilter: isGlass ? 'blur(20px)' : undefined,
        }}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div
            className="flex items-center gap-2 px-2 py-1.5 border-b"
            style={{
              backgroundColor: config.theme.accentOnHeader ? `${primaryColor}15` : 'transparent',
              borderColor: isDark ? '#374151' : '#e5e7eb',
            }}
          >
            <div className="w-4 h-4">
              <GenieAvatar color={primaryColor} size={16} customAvatarUrl={avatarUrl} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold truncate" style={{ color: textColor }}>
                {genie.name}
              </p>
              <p className="text-[8px]" style={{ color: subtextColor }}>
                Click to start
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col items-center justify-center p-2">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
              style={{ backgroundColor: `${primaryColor}15` }}
            >
              <GenieAvatar color={primaryColor} size={44} customAvatarUrl={avatarUrl} />
            </div>
            <p className="text-[8px] text-center mb-2" style={{ color: subtextColor }}>
              {genie.name}
            </p>
            <button
              className="flex items-center gap-1 px-2 py-1 rounded-full text-white text-[8px] font-medium"
              style={{ backgroundColor: primaryColor }}
            >
              <Mic className="w-2 h-2" />
              Start Chat
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <button
        onClick={() => setIsPreviewOpen(!isPreviewOpen)}
        className="flex items-center justify-between w-full text-left mb-3"
      >
        <h3 className="text-sm font-semibold text-gray-700">Live Preview</h3>
        {isPreviewOpen ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {isPreviewOpen && (
        <div className="space-y-4">
          {/* Closed State Preview */}
          <div>
            <p className="text-xs text-gray-600 mb-2">Closed state:</p>
            <div
              className="relative rounded-lg shadow-lg overflow-hidden"
              style={{ height: selectedTemplate === 'banner' ? 160 : 140 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
                {/* Mini browser chrome */}
                <div className="flex items-center gap-2 p-2 bg-white border-b">
                  <div className="w-2 h-2 bg-red-400 rounded-full" />
                  <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                  <div className="w-2 h-2 bg-green-400 rounded-full" />
                  <div className="flex-1 bg-gray-100 rounded h-4 ml-4" />
                </div>
                {/* Fake content */}
                <div className="p-3">
                  <div className="h-2 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-2 bg-gray-200 rounded w-1/2 mb-3" />
                  <div className="h-12 bg-gray-100 rounded" />
                </div>
              </div>
              {renderButton()}
              {renderCaption()}
            </div>
          </div>

          {/* Open State Preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-600">Open state:</p>
              {!chatOnly && <div className="flex items-center rounded-lg bg-gray-100 p-0.5">
                <button
                  type="button"
                  onClick={() => setOpenPreviewTab('start')}
                  className={cn(
                    'px-2 py-1 rounded-md text-[11px] font-medium transition-colors',
                    openPreviewTab === 'start' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  Start screen
                </button>
                <button
                  type="button"
                  onClick={() => setOpenPreviewTab('talking')}
                  className={cn(
                    'px-2 py-1 rounded-md text-[11px] font-medium transition-colors',
                    openPreviewTab === 'talking' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  Talking
                </button>
                <button
                  type="button"
                  onClick={() => setOpenPreviewTab('chat')}
                  className={cn(
                    'px-2 py-1 rounded-md text-[11px] font-medium transition-colors',
                    openPreviewTab === 'chat' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  Chat view
                </button>
              </div>}
            </div>
            <div
              className="relative bg-white rounded-lg shadow-lg overflow-hidden"
              // Tall enough for the popup itself (now sized to match the real
              // widget's proportions) plus its bottom-2 offset — a fixed 240px
              // clipped the popup's footer once the avatar/CTA grew to their
              // true production size.
              style={{ height: Math.round(180 * (580 / 380)) + 40 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
                {/* Mini browser chrome */}
                <div className="flex items-center gap-2 p-2 bg-white border-b">
                  <div className="w-2 h-2 bg-red-400 rounded-full" />
                  <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                  <div className="w-2 h-2 bg-green-400 rounded-full" />
                  <div className="flex-1 bg-gray-100 rounded h-4 ml-4" />
                </div>
                {/* Fake content */}
                <div className="p-3">
                  <div className="h-2 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-2 bg-gray-200 rounded w-1/2 mb-3" />
                  <div className="h-12 bg-gray-100 rounded" />
                </div>
              </div>
              {renderPopup()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetPreview;
